import {
  unifiedSetDoc,
  unifiedUpdateDoc,
  unifiedDeleteDoc,
  unifiedGetDoc,
  unifiedCollection,
  movieCacheService,
} from '@watchlist/shared';
import type { MovieCacheData } from '@watchlist/shared/src/types/movieCache';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BaseStory {
  id: string;
  authorUid: string;
  createdAt: string;
  expiresAt: string;
  contentVersion: number;
  viewCount: number;
}

export interface ReviewStory extends BaseStory {
  kind: 'review';
  movieId: string;
  movieTitle: string;
  posterPath: string | null;
  backdropPath: string | null;
  director: string | null;
  releaseYear: number | null;
  /** ID of the userMovies document — used as comment/like parent. */
  reviewId: string;
  rating: number | null;
  reviewText: string;
  watchedDate?: string;
  watchedDatePrecision?: 'day' | 'month' | 'year';
}

export interface WatchlistStory extends BaseStory {
  kind: 'watchlist';
  watchlistId: string;
  watchlistName: string;
  description?: string;
  coverPath: string | null;
  topPosterPaths: string[];
  movieCount: number;
}

/** Tagged union. Documents read without a `kind` field are coerced to 'review'. */
export type Story = ReviewStory | WatchlistStory;

export interface StoryView {
  viewedAt: string;
  viewedContentVersion: number;
  displayName: string;
  photoURL: string | null;
}

export interface AuthorStories {
  authorUid: string;
  stories: Story[];
  hasUnseen: boolean;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const STORIES_COLLECTION = 'stories';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function reviewStoryId(authorUid: string, movieId: string): string {
  return `${authorUid}_review_${movieId}`;
}

function watchlistStoryId(authorUid: string, watchlistId: string): string {
  return `${authorUid}_watchlist_${watchlistId}`;
}

/** The userMovies document ID — stable, used as comment/like parent. */
function reviewDocId(authorUid: string, movieId: string): string {
  return `${authorUid}_${movieId}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

function expiresAtIso(): string {
  return new Date(Date.now() + SEVEN_DAYS_MS).toISOString();
}

/** Coerce a raw Firestore document to the Story union. */
function coerceStory(id: string, data: Record<string, unknown>): Story {
  const kind = (data.kind as string | undefined) ?? 'review';
  if (kind === 'watchlist') {
    return {
      id,
      kind: 'watchlist',
      authorUid: data.authorUid as string,
      createdAt: data.createdAt as string,
      expiresAt: data.expiresAt as string,
      contentVersion: (data.contentVersion as number) ?? 1,
      viewCount: (data.viewCount as number) ?? 0,
      watchlistId: data.watchlistId as string,
      watchlistName: data.watchlistName as string,
      description: (data.description as string | undefined) ?? undefined,
      coverPath: (data.coverPath as string | null) ?? null,
      topPosterPaths: (data.topPosterPaths as string[]) ?? [],
      movieCount: (data.movieCount as number) ?? 0,
    };
  }
  return {
    id,
    kind: 'review',
    authorUid: data.authorUid as string,
    createdAt: data.createdAt as string,
    expiresAt: data.expiresAt as string,
    contentVersion: (data.contentVersion as number) ?? 1,
    viewCount: (data.viewCount as number) ?? 0,
    movieId: data.movieId as string,
    movieTitle: data.movieTitle as string,
    posterPath: (data.posterPath as string | null) ?? null,
    backdropPath: (data.backdropPath as string | null) ?? null,
    director: (data.director as string | null) ?? null,
    releaseYear: (data.releaseYear as number | null) ?? null,
    reviewId: (data.reviewId as string) ?? reviewDocId(data.authorUid as string, data.movieId as string),
    rating: (data.rating as number | null) ?? null,
    reviewText: (data.reviewText as string) ?? '',
    watchedDate: (data.watchedDate as string | undefined) ?? undefined,
    watchedDatePrecision: (data.watchedDatePrecision as 'day' | 'month' | 'year' | undefined) ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Write operations
// ---------------------------------------------------------------------------

export async function createStory(params: {
  authorUid: string;
  movieId: string;
  rating: number | null;
  reviewText: string;
  watchedDate?: string;
  watchedDatePrecision?: 'day' | 'month' | 'year';
}): Promise<string> {
  const { authorUid, movieId, rating, reviewText, watchedDate, watchedDatePrecision } = params;
  const storyId = reviewStoryId(authorUid, movieId);
  const reviewId = reviewDocId(authorUid, movieId);

  let movieTitle = movieId;
  let posterPath: string | null = null;
  let backdropPath: string | null = null;
  let director: string | null = null;
  let releaseYear: number | null = null;

  try {
    let movieData: MovieCacheData = await movieCacheService.getMovieData(movieId);
    // Cache entry might pre-date backdropPath storage — force a fresh TMDB fetch
    if (!movieData.backdropPath) {
      movieData = await movieCacheService.refreshMovieData(movieId);
    }
    movieTitle = movieData.title;
    posterPath = movieData.posterPath;
    backdropPath = movieData.backdropPath;
    director = movieData.directors?.[0]?.name ?? null;
    releaseYear = movieData.year ? parseInt(movieData.year, 10) : null;
  } catch (err) {
    console.warn(`📖 storiesService: Could not fetch movie cache for ${movieId}`, err);
  }

  const story: Omit<ReviewStory, 'id'> = {
    kind: 'review',
    authorUid,
    movieId,
    movieTitle,
    posterPath,
    backdropPath,
    director,
    releaseYear,
    reviewId,
    rating,
    reviewText,
    ...(watchedDate ? { watchedDate } : {}),
    ...(watchedDatePrecision ? { watchedDatePrecision } : {}),
    createdAt: nowIso(),
    expiresAt: expiresAtIso(),
    contentVersion: 1,
    viewCount: 0,
  };

  await unifiedSetDoc(STORIES_COLLECTION, storyId, story, false);
  console.log(`✅ storiesService: Created review story ${storyId}`);
  return storyId;
}

export async function createWatchlistStory(params: {
  authorUid: string;
  watchlistId: string;
  watchlistName: string;
  description?: string;
  coverPath: string | null;
  topPosterPaths: string[];
  movieCount: number;
}): Promise<string> {
  const { authorUid, watchlistId, watchlistName, description, coverPath, topPosterPaths, movieCount } = params;
  const storyId = watchlistStoryId(authorUid, watchlistId);

  const story: Omit<WatchlistStory, 'id'> = {
    kind: 'watchlist',
    authorUid,
    watchlistId,
    watchlistName,
    ...(description ? { description } : {}),
    coverPath,
    topPosterPaths,
    movieCount,
    createdAt: nowIso(),
    expiresAt: expiresAtIso(),
    contentVersion: 1,
    viewCount: 0,
  };

  await unifiedSetDoc(STORIES_COLLECTION, storyId, story, false);
  console.log(`✅ storiesService: Created watchlist story ${storyId}`);
  return storyId;
}

export async function updateStoryForReview(params: {
  authorUid: string;
  movieId: string;
  rating: number | null;
  reviewText: string;
}): Promise<void> {
  const { authorUid, movieId, rating, reviewText } = params;
  const storyId = reviewStoryId(authorUid, movieId);

  let movieMetaUpdate: Record<string, unknown> = {};
  try {
    let movieData: MovieCacheData = await movieCacheService.getMovieData(movieId);
    if (!movieData.backdropPath) {
      movieData = await movieCacheService.refreshMovieData(movieId);
    }
    movieMetaUpdate = {
      movieTitle: movieData.title,
      posterPath: movieData.posterPath,
      backdropPath: movieData.backdropPath,
      director: movieData.directors?.[0]?.name ?? null,
      releaseYear: movieData.year ? parseInt(movieData.year, 10) : null,
    };
  } catch {
    // Non-critical — update proceeds without refreshing movie metadata
  }

  await unifiedUpdateDoc(STORIES_COLLECTION, storyId, {
    ...movieMetaUpdate,
    rating,
    reviewText,
    contentVersion: Date.now(),
  });
  console.log(`✅ storiesService: Updated review story ${storyId}`);
}

export async function deleteStoryForReview(authorUid: string, movieId: string): Promise<void> {
  const storyId = reviewStoryId(authorUid, movieId);
  try {
    await unifiedDeleteDoc(STORIES_COLLECTION, storyId);
    console.log(`✅ storiesService: Deleted review story ${storyId}`);
  } catch (err) {
    console.warn(`storiesService: deleteStoryForReview ${storyId} no-op or error`, err);
  }
}

export async function deleteStoryForWatchlist(authorUid: string, watchlistId: string): Promise<void> {
  const storyId = watchlistStoryId(authorUid, watchlistId);
  try {
    await unifiedDeleteDoc(STORIES_COLLECTION, storyId);
    console.log(`✅ storiesService: Deleted watchlist story ${storyId}`);
  } catch (err) {
    console.warn(`storiesService: deleteStoryForWatchlist ${storyId} no-op or error`, err);
  }
}

export async function markStoryViewed(
  storyId: string,
  contentVersion: number,
  viewer: { uid: string; displayName: string; photoURL: string | null }
): Promise<void> {
  const view: StoryView = {
    viewedAt: nowIso(),
    viewedContentVersion: contentVersion,
    displayName: viewer.displayName,
    photoURL: viewer.photoURL,
  };
  await unifiedSetDoc(`${STORIES_COLLECTION}/${storyId}/views`, viewer.uid, view, true);
}

// ---------------------------------------------------------------------------
// Read operations
// ---------------------------------------------------------------------------

export async function getCrewStoriesFeed(crewUids: string[]): Promise<Story[]> {
  if (crewUids.length === 0) return [];

  const nowIsoStr = new Date().toISOString();
  const CHUNK_SIZE = 30;
  const chunks: string[][] = [];
  for (let i = 0; i < crewUids.length; i += CHUNK_SIZE) {
    chunks.push(crewUids.slice(i, i + CHUNK_SIZE));
  }

  const allStories: Story[] = [];
  for (const chunk of chunks) {
    const docs = await unifiedCollection(STORIES_COLLECTION)
      .where('authorUid', 'in', chunk)
      .where('expiresAt', '>', nowIsoStr)
      .orderBy('expiresAt', 'asc')
      .getDocs();

    for (const doc of docs) {
      allStories.push(coerceStory(doc.id, doc.data() as Record<string, unknown>));
    }
  }

  allStories.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return allStories;
}

export async function getMyStories(authorUid: string): Promise<Story[]> {
  const nowIsoStr = new Date().toISOString();
  const docs = await unifiedCollection(STORIES_COLLECTION)
    .where('authorUid', '==', authorUid)
    .where('expiresAt', '>', nowIsoStr)
    .orderBy('expiresAt', 'asc')
    .getDocs();

  return docs.map((doc) => coerceStory(doc.id, doc.data() as Record<string, unknown>));
}

export async function hasEverPostedStory(authorUid: string): Promise<boolean> {
  const docs = await unifiedCollection(STORIES_COLLECTION)
    .where('authorUid', '==', authorUid)
    .limit(1)
    .getDocs();
  return docs.length > 0;
}

export async function getStoryViewers(storyId: string): Promise<Array<StoryView & { uid: string }>> {
  const docs = await unifiedCollection(`${STORIES_COLLECTION}/${storyId}/views`)
    .orderBy('viewedAt', 'desc')
    .getDocs();

  return docs.map((doc) => ({ uid: doc.id, ...doc.data() } as StoryView & { uid: string }));
}

export async function getViewedVersions(
  viewerUid: string,
  storyIds: string[]
): Promise<Map<string, number>> {
  const result = new Map<string, number>();

  await Promise.all(
    storyIds.map(async (storyId) => {
      try {
        const snap = await unifiedGetDoc(`${STORIES_COLLECTION}/${storyId}/views`, viewerUid);
        if (snap.exists()) {
          const data = snap.data() as StoryView;
          result.set(storyId, data.viewedContentVersion);
        }
      } catch {
        // Not viewed — leave out of map (treated as unseen)
      }
    })
  );
  return result;
}

/** Returns the Firestore doc ID for a watchlist story (used for existence checks). */
export function getWatchlistStoryId(authorUid: string, watchlistId: string): string {
  return watchlistStoryId(authorUid, watchlistId);
}

/** Returns the Firestore doc ID for a review story (used for existence checks). */
export function getReviewStoryId(authorUid: string, movieId: string): string {
  return reviewStoryId(authorUid, movieId);
}

/**
 * Heals the current user's own stories that are missing a backdropPath.
 * Runs at most once per app session (tracked in module-level flag).
 * Call on app open — non-blocking, safe to fire-and-forget.
 */
let _backdropHealDone = false;

export async function healStoriesBackdrops(authorUid: string): Promise<void> {
  if (_backdropHealDone) return;
  _backdropHealDone = true;

  try {
    const stories = await getMyStories(authorUid);
    const broken = stories.filter(
      (s): s is ReviewStory => s.kind === 'review' && !(s as ReviewStory).backdropPath
    );
    if (broken.length === 0) return;

    console.log(`🩹 storiesService: healing ${broken.length} stories missing backdropPath`);
    for (const story of broken) {
      try {
        const movieData = await movieCacheService.refreshMovieData(story.movieId);
        if (movieData.backdropPath) {
          await unifiedUpdateDoc(STORIES_COLLECTION, story.id, {
            backdropPath: movieData.backdropPath,
            posterPath: movieData.posterPath ?? story.posterPath,
          });
          console.log(`✅ Healed backdropPath for story ${story.id}`);
        }
      } catch (err) {
        console.warn(`storiesService: could not heal story ${story.id}`, err);
      }
    }
  } catch (err) {
    console.warn('storiesService: healStoriesBackdrops failed', err);
  }
}
